import { apiFetch, isApiMode } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE ORGANIZATION'S OWN «LOGG» — the client (#935, 2026-11-01).
//
// `GET /organization/audit` is tenant-scoped: the organization is the one the
// portal is acting in (the tenant header `apiFetch` sets), never a parameter.
// The server decides who may read (`organization.audit.read`, Eier and
// Administrator by default), what of a row leaves it (no free text, StarPoint
// shown as StarPoint) and records every read. This sends a window and an
// optional action filter and renders the answer. It never throws, and it
// carries refusal CODES, never sentences.
// ─────────────────────────────────────────────────────────────────────

/**
 * The capability the route asks for. The browser uses it for ONE thing:
 * deciding whether to DRAW the «Logg» entry in the settings. The server
 * refuses everybody else regardless.
 */
export const ORGANIZATION_AUDIT_READ = 'organization.audit.read';

/** Who did it, as the organization is shown it. */
export type OrganizationAuditActor =
  | { kind: 'person'; id: string | null }
  | { kind: 'member'; id: string | null }
  /** A person who is not on the roster, booking here as a guest (#833). */
  | { kind: 'guest'; id: string | null }
  | { kind: 'starpoint' }
  | { kind: 'device'; id: string | null }
  | { kind: 'system' }
  | { kind: 'provider' };

export interface OrganizationAuditEvent {
  id: string;
  /** ISO instant. */
  at: string;
  /** The dotted action code, e.g. `organization_role.grant`. */
  action: string;
  actor: OrganizationAuditActor;
  subjectType: string | null;
  subjectId: string | null;
}

export interface OrganizationAuditPage {
  events: OrganizationAuditEvent[];
  nextCursor: string | null;
  /** The window the server applied — `from` is set even when none was asked. */
  window: { from: string | null; to: string | null };
  /** Display names for the account ids on the page. */
  accounts: Record<string, { displayName: string | null }>;
}

export interface OrganizationAuditFilters {
  /** Exact, or a prefix ending in `.`. */
  action?: string;
  /** ISO instants with an offset. */
  from?: string;
  to?: string;
}

export type OrganizationAuditResult =
  | { state: 'no-api' }
  | { state: 'ok'; page: OrganizationAuditPage }
  /** No session, or one without `organization.audit.read` here. */
  | { state: 'forbidden' }
  /** A filter the server refused; `field` names it. */
  | { state: 'refused'; code: string | null; field: string | null }
  | { state: 'error'; status: number | null };

/** The query string. Only non-empty filters are sent. */
export function organizationAuditQuery(
  filters: OrganizationAuditFilters,
  cursor?: string | null,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    const v = typeof value === 'string' ? value.trim() : '';
    if (v !== '') params.set(key, v);
  }
  if (cursor) params.set('cursor', cursor);
  const q = params.toString();
  return q ? `?${q}` : '';
}

/** One page of the organization's log. Every call is recorded by the server as a read. */
export async function fetchOrganizationAudit(
  filters: OrganizationAuditFilters,
  cursor?: string | null,
): Promise<OrganizationAuditResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(`/organization/audit${organizationAuditQuery(filters, cursor)}`);
    if (res.ok) return { state: 'ok', page: (await res.json()) as OrganizationAuditPage };
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status >= 500) return { state: 'error', status: res.status };
    const body = (await res.json().catch(() => null)) as {
      error?: unknown;
      field?: unknown;
    } | null;
    return {
      state: 'refused',
      code: typeof body?.error === 'string' ? body.error : null,
      field: typeof body?.field === 'string' ? body.field : null,
    };
  } catch {
    return { state: 'error', status: null };
  }
}
